import React from "react";
import { observer } from "mobx-react";
import "./UserItem.scss";
import { User, UserStatus } from "../../../../stores/AuthStore/types";
import { Button, ButtonGroup, CircularProgress } from "@mui/material";
import useTranslation from "../../../../hooks/useTranslation";
import { useState } from "react";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Profile from "../../../Profile";
import authStore from "../../../../stores/AuthStore";
import feedbackStore from "../../../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../../../stores/FeedbackStore/types";

interface UserItemProps {
  user: User;
  afterUpdateUser?: (user: User) => void;
}

const UserItem = observer(({ user, afterUpdateUser }: UserItemProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleOnBlockChange = () => {
    const status =
      user.status === UserStatus.ACTIVE
        ? UserStatus.BLOCKED
        : UserStatus.ACTIVE;
    setIsLoading(true);
    authStore
      .updateUser({
        ...user,
        status,
      })
      .then(() => {
        user.status = status;
      })
      .catch((error) => {
        console.log(error);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "connection-error",
        });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className={`user dashboardItem ${user.status.toLowerCase()}`}>
      <header>
        <p>
          <b>{t("user-id")}:</b>&nbsp;
          {user.id}
        </p>
        <p>
          <b>
            {t("first-name")} {t("last-name")}:
          </b>
          &nbsp;
          {user.firstName} {user.lastName}
        </p>
      </header>
      <main>
        <p>
          <i className={"fal fa-envelope"} />
          &nbsp;&nbsp;
          <a href={`mailto:${user.email}`}>{user.email}</a>
        </p>
        <p>
          <i className={"fal fa-phone"} />
          &nbsp;&nbsp;
          <a href={`tel:${user.phone}`}>{user.phone}</a>
        </p>
        <p>
          <i className={"fal fa-user"} />
          &nbsp;&nbsp;
          {user.roles?.map((role) => t(role.toLowerCase())).join(",")}
        </p>
      </main>
      <footer className={"pt"}>
        <ButtonGroup
          fullWidth
          variant={user.status === UserStatus.ACTIVE ? "outlined" : "contained"}
        >
          <Button onClick={() => setOpen(true)}>
            <i className="fal fa-edit" />
            &nbsp;
            {t("edit")}
          </Button>
          <Button
            color={user.status === UserStatus.ACTIVE ? "error" : "secondary"}
            onClick={handleOnBlockChange}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={"16px"} />
            ) : (
              <>
                {user.status === UserStatus.ACTIVE && (
                  <i className="fal fa-ban" />
                )}
                &nbsp;
                {user.status === UserStatus.BLOCKED && t("unblock")}
                {user.status === UserStatus.DEACTIVE && t("activate-user")}
              </>
            )}
          </Button>
        </ButtonGroup>
      </footer>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="adminUserItemDialog"
      >
        <DialogActions className="dialogActions">
          <Button onClick={() => setOpen(false)} color={"error"}>
            &times;
          </Button>
        </DialogActions>
        <DialogContent sx={{ pt: { xs: 1, sm: 3 } }}>
          <Profile controlledUser={user} afterUpdateUser={afterUpdateUser} />
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default UserItem;
